import { Modal } from "react-bootstrap";
import { useEffect, useState } from "react";
import { HeaderText } from "./HeaderText";
import { Button } from "./Button";
import { AddVehicleForm } from "./AddVehicleModal";
import { SelectOption } from "./SelectOption";
import { showStateName, getStateEnumFromCode } from "../Data/UsState";
import { Vehicle } from "../Data/ApiTypes";

export interface ChooseVehicleModalProps {
    show:     boolean;
    setShow:  ( b: boolean ) => void;
    vehicles: Vehicle[];
    exclude?: number[];
    onSubmit: ( v: Vehicle ) => void;
    onClose: () => void;
    /** Set false when opened over another modal to avoid a double mask. */
    backdrop?: boolean | "static";
    /** Defaults to "Choose a vehicle". */
    title?: string;
    planName?: string;
    facilityName?: string;
    /** Create a vehicle without leaving this flow. Opens another modal when set. */
    onAddNew?: () => void;
    /** Create a vehicle in this modal, then hand it to the caller. */
    onCreate?: ( v: Vehicle ) => void;
}

export function ChooseVehicleModal( props: ChooseVehicleModalProps ) {
    const { vehicles, show, setShow, onSubmit, onClose, exclude = [], backdrop = true, title = "Choose a vehicle", planName, facilityName, onAddNew, onCreate } = { ...props };
    const [adding, setAdding] = useState( false );
    const [selectedId, setSelectedId] = useState<number | null>( null );

    useEffect(() => {
        if( !show ) {
            setAdding( false );
            setSelectedId( null );
        }
    }, [show]);

    function submit( v: Vehicle ) {
        onSubmit( v );
        setShow( false );
    }
    const filtered = vehicles.filter( v => !exclude.includes( v.vehicleId ) );
    const etc = vehicles.length !== 0 && filtered.length === 0;
    const canAdd = onCreate != null || onAddNew != null;
    const selected = filtered.find( v => v.vehicleId === selectedId ) ?? null;

    function startAdd() {
        if( onCreate )
            setAdding( true );
        else
            onAddNew?.();
    }

    return <Modal show={show} onHide={onClose} centered backdrop={backdrop}>
        <Modal.Header closeButton>
            <HeaderText>{title}</HeaderText>
        </Modal.Header>
        <Modal.Body>
            {(planName || facilityName) && (
                <div className="rounded-3 p-3 mb-3 d-flex align-items-center gap-2" style={{ background: "#FDE8F3" }}>
                    <div className="rounded-2 d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                         style={{ width: 28, height: 28, background: "#FC5EB4", color: "#fff", fontSize: 14 }}>
                        P
                    </div>
                    <div className="min-w-0">
                        {planName && <div className="fw-semibold">{planName}</div>}
                        {facilityName && <div className="text-secondary small">{facilityName}</div>}
                    </div>
                </div>
            )}
            {adding && onCreate ? (
                <AddVehicleForm
                    autoFocus
                    submitText="Add and assign"
                    cancelText="Back"
                    onSubmit={onCreate}
                    onCancel={() => setAdding( false )}
                />
            ) : (
            <>
            {(planName || facilityName) && filtered.length > 0 && (
                <div className="fw-semibold mb-2">Choose a vehicle</div>
            )}
            {vehicles.length === 0 && <div className="text-secondary small mb-2">You haven&apos;t added any vehicles.</div>}
            {etc && <div className="text-secondary small mb-2">Every vehicle is already on a subscription.</div>}
            <div className="d-flex flex-column gap-2">
                {filtered.map( v => {
                    const lines = vehicleLines( v );
                    const color = v.color && v.color !== "N/A" ? v.color : "#E5E5E5";
                    return <SelectOption key={v.vehicleId} selected={v.vehicleId === selectedId} onClick={() => setSelectedId( v.vehicleId )}>
                        <div className="rounded-2 border flex-shrink-0"
                             style={{ height: 32, width: 32, backgroundColor: color }} />
                        <div className="min-w-0">
                            <div className="fw-semibold">{lines.title}</div>
                            {lines.sub && <div className="text-secondary small">{lines.sub}</div>}
                        </div>
                    </SelectOption>;
                } )}
            </div>
            {canAdd && (
                <button type="button"
                        className="w-100 mt-3 py-2 bg-white text-secondary"
                        style={{ border: "1px dashed #D0D0D0", borderRadius: 12 }}
                        onClick={startAdd}>
                    + Add a new vehicle
                </button>
            )}
            <Button type="button"
                    className="w-100 btn-main mt-3"
                    disabled={selected == null}
                    onClick={() => { if( selected ) submit( selected ); }}>
                {selected ? `Assign ${vehicleLines( selected ).title}` : "Assign"}
            </Button>
            <button type="button"
                    className="btn btn-link w-100 mt-1 text-secondary text-decoration-none"
                    onClick={() => setShow( false )}>
                Cancel
            </button>
            </>
            )}
        </Modal.Body>
    </Modal>;
}

function isReal( value: string | undefined | null ) {
    return value != null && value !== "" && value !== "N/A";
}

function vehicleLines( vehicle: Vehicle ) {
    const name = [vehicle.make, vehicle.model].filter( isReal ).join( " " );
    const state = showStateName( getStateEnumFromCode( vehicle.stateCode )! );
    const plate = vehicle.licensePlateNumber;
    if( name )
        return { title: name, sub: [state, plate].filter( Boolean ).join( " · " ) };
    return { title: plate, sub: state ?? "" };
}